<template>
  <div :class="{ mobile: isMobile }">
    <h2>
      Review the routing information of your network
    </h2>
    <div class="subtitle-container">
      <h6>Prevent network misconfigurations and detect BGP hijacks.</h6>
      <fa
        v-if="!isExpandAbout && !isMobile"
        icon="question-circle"
        v-tooltip.top="{ content: 'About this page' }"
        @click="isExpandAbout = true"
      ></fa>
      <fa
        v-if="isExpandAbout && !isMobile"
        icon="chevron-up"
        @click="isExpandAbout = false"
      ></fa>
    </div>
    <div
      v-if="isMobile"
      class="about-toggle"
      @click="isExpandAbout = !isExpandAbout"
    >
      <p>About this page</p>
      <div class="icon-wrapper">
        <fa
          icon="chevron-down"
          :class="{ 'fa-rotate-180': isExpandAbout }"
        ></fa>
      </div>
    </div>
    <div class="about-content" :class="{ show: isExpandAbout }">
      <h5 v-if="!isMobile">About this page</h5>
      <p>This page displays:</p>
      <ul>
        <li>
          <span>
            Status of the BGP announcements for each prefix.
          </span>
          <span class="show-modal" @click="showFirstAboutModal">
            Why is it important to announce prefixes in BGP?
          </span>
        </li>
        <li>
          <span>
            Inconsistencies among your BGP announcements, RPKI ROAs and IRR
            Route Objects.
          </span>
          <span class="show-modal" @click="showSecondAboutModal">
            Why is it important that BGP is consistent with RPKI ROAs and IRR
            Route Objects?
          </span>
        </li>
      </ul>
      <div>
        <h6>
          At this time, APNIC only reports on the state of IRR against the
          following IRR sources:
        </h6>
        <p>
          APNIC, LACNIC, ARIN, RIPE, AFRINIC, JPIRR, JPNIC, KRNIC and TWNIC.
        </p>
      </div>
    </div>

    <ModalContainer
      v-if="isShowFirstAboutModal"
      modalTitle="Why is it important to announce prefixes in BGP?"
      closeBtnText="Close"
      @confirm="closeFirstAboutModal"
      @closeModal="closeFirstAboutModal"
    >
      <template slot="body">
        <p>
          If you don't announce your internet address space in BGP, you leave it
          open for somebody else to do so. Not declaring any routing intent
          allows bad actors to announce ranges of your space, without visible
          conflict in routing. The absence of an RPSL route object or a ROA
          (Route Origin Authorization) does not prevent some BGP speakers from
          seeing these announcements, and so does not defend against
          unauthorised use of your space.
        </p>
        <p>
          If you do not wish space to be routed, publishing an AS0 ROA in RPKI
          can help identify that you do not wish it to be seen, except from any
          origin-AS you authorise in another ROA. Therefore, not declaring any
          other ROA except an AS0 ROA will formally declare you do not wish this
          space routed at this time.
        </p>
        <p>
          If you publish an RPSL route object, or a ROA which covers address
          space you do not originate in BGP, a form of attack is possible where
          a bad actor announces the more specific not seen from you, but with
          the origin-AS as your AS. Since this will be the longest match, it
          becomes the best path. They may or may not pass the traffic on, so
          this risk can be either "snooping" or re-routing to an illicit point
          inside their control, depending on how they chose to operate.
        </p>
        <p>
          By announcing all your space, and publishing a ROA, you limit the risk
          to a smaller horizon of BGP speakers "close" to any bad actor who see
          and propagate any false announcement. Be careful to limit use of
          MaxLength, since this can introduce the same risk as not announcing,
          for any more specific prefix legal under the maxlength setting you do
          not actually announce.
        </p>
        <p>
          By announcing all your space, and publishing a ROA, you limit the risk
          to a smaller horizon of BGP speakers "close" to any bad actor who see
          and propagate any false announcement. Be careful to limit use of
          MaxLength, since this can introduce the same risk as not announcing,
          for any more specific prefix legal under the maxlength setting you do
          not actually announce.
        </p>
      </template>
    </ModalContainer>

    <ModalContainer
      v-if="isShowSecondAboutModal"
      modalTitle="Why is it important that BGP is consistent with RPKI ROAs and IRR route objects?"
      closeBtnText="Close"
      @confirm="closeSecondAboutModal"
      @closeModal="closeSecondAboutModal"
    >
      <template slot="body">
        <p>
          Operators of BGP routing systems use both RPKI ROA and IRR Route
          Object to determine local filter policy against BGP updates seen from
          their peers.
        </p>
        <p>
          If your statements in RPKI ROA and IRR route object do not align (have
          different prefixes, different origin-AS) then your BGP will be
          inconsistently interpreted depending on which filtering system is
          used, by other BGP speakers. This can be extremely hard to debug, from
          network visible traces and BGP seen worldwide since the specifics of
          which filtering mechanism has been used is not visible.
        </p>
        <p>
          The recommendation is to keep your BGP aligned with your external
          declarations of intention: make sure every origin-AS and prefix length
          you announce is declared in RPSL route objects in the IRR, or in a ROA
          (Route Origin Authorization), or in both. And if you use both, make
          sure the declarations agree with each other.
        </p>
      </template>
    </ModalContainer>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ModalContainer from '../ui/ModalContainer.vue';

export default {
  name: 'Header',
  components: {
    ModalContainer
  },
  data() {
    return {
      isExpandAbout: false,
      isShowFirstAboutModal: false,
      isShowSecondAboutModal: false
    };
  },
  computed: {
    ...mapGetters(['isMobile'])
  },
  methods: {
    closeFirstAboutModal() {
      this.isShowFirstAboutModal = false;
    },
    showFirstAboutModal() {
      this.isShowFirstAboutModal = true;
    },
    closeSecondAboutModal() {
      this.isShowSecondAboutModal = false;
    },
    showSecondAboutModal() {
      this.isShowSecondAboutModal = true;
    }
  }
};
</script>

<style lang="scss" scoped>
h2 {
  margin: 0;
  margin-bottom: 1rem;
  line-height: 2rem;
  color: $text-blue;
  font-size: 1.75rem;
}

.subtitle-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  h6 {
    margin: 0;
  }

  svg {
    display: inline-block;
    margin-right: 0.5rem;
    font-size: 1.25rem;
    color: $icon-grey;
    cursor: pointer;
  }
}

.about-toggle {
  display: flex;
  align-items: center;
  margin: 0;
  width: 12rem;

  p {
    margin: 0;
    border-bottom: 1px solid $text-black;
    cursor: pointer;
  }

  .icon-wrapper {
    padding: 0.5rem;
    padding-right: 1.5rem;
    cursor: pointer;

    svg {
      transition: transform 0.5s;
    }
  }
}

.about-content {
  border: none;
  padding: 0;
  opacity: 0;
  height: auto;
  max-height: 0;
  overflow: hidden;
  transition-property: opacity max-height;
  transition-duration: 0.5s;

  &.show {
    opacity: 1;
    padding-top: 1rem;
    max-height: 50rem;
    border-top: 1px solid #bbb;
    border-bottom: 1px solid #bbb;
  }

  ul {
    list-style-position: inside;
  }

  li {
    margin-bottom: 1rem;
  }

  .show-modal {
    display: block;
    margin-top: 0.5rem;
    color: $text-light-blue;
    cursor: pointer;
  }

  h6 {
    margin-bottom: 0.25rem;
  }
}

.mobile {
  h2 {
    font-size: 1.25rem;
  }

  .about-content {
    border: none;
  }
}
</style>
